import React, { useEffect, useMemo, useState } from "react";
import { Download, FileText, Receipt, Search, Wallet, Clock3 } from "lucide-react";
import { useInvoices } from "../hooks/useInvoices";
import {
  EmptyState,
  ErrorState,
  FilterTabs,
  LoadingState,
  MetricCard,
  PageHeader,
  StatusPill,
  formatDate,
  formatMoney,
} from "../components/PageKit";

const STATUS = {
  pending: { label: "À payer", tone: "warning" },
  paid: { label: "Payée", tone: "success" },
  failed: { label: "Échec de paiement", tone: "danger" },
  cancelled: { label: "Annulée", tone: "neutral" },
  refunded: { label: "Remboursée", tone: "info" },
};

const TYPES = {
  STORE_DEBIT: { label: "Débit store", tone: "danger" },
  PROVIDER_CREDIT: { label: "Crédit prestataire", tone: "success" },
  SAAS_CREDIT: { label: "Frais plateforme", tone: "brand" },
};

export default function AdminMarketplaceInvoices() {
  const { invoices, stats, loading, error, fetchInvoices, fetchStats, downloadPDF } = useInvoices();
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, [fetchInvoices, fetchStats]);

  const counts = useMemo(() => {
    const out = { all: invoices.length };
    invoices.forEach((inv) => {
      out[inv.status] = (out[inv.status] || 0) + 1;
    });
    return out;
  }, [invoices]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices.filter(
      (inv) =>
        (status === "all" || inv.status === status) &&
        (!type || inv.invoice_type === type) &&
        (!q || [inv.invoice_number, inv.order_reference, inv.order_id].some((v) => String(v || "").toLowerCase().includes(q)))
    );
  }, [invoices, status, type, search]);

  const download = async (id) => {
    setDownloading(id);
    try {
      await downloadPDF(id);
    } finally {
      setDownloading(null);
    }
  };

  if (loading && !invoices.length) return <LoadingState label="Chargement des factures…" />;
  if (error && !invoices.length) return <ErrorState error={error} title="Factures indisponibles" onRetry={() => fetchInvoices()} />;

  const currency = stats?.currency || "MAD";

  return (
    <div className="page-shell space-y-5">
      <PageHeader
        eyebrow="Marketplace"
        title="Factures"
        subtitle="Chaque commande livrée par un prestataire génère une facture : la commission est prélevée sur votre portefeuille."
      />

      {stats && (
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <MetricCard icon={FileText} label="Factures" value={stats.total_invoices ?? 0} />
          <MetricCard icon={Receipt} label="Montant total" value={formatMoney(stats.total_amount, currency)} tone="info" />
          <MetricCard icon={Wallet} label="Payé" value={formatMoney(stats.paid_amount, currency)} tone="success" />
          <MetricCard icon={Clock3} label="À payer" value={formatMoney(stats.pending_amount, currency)} tone="warning" />
        </div>
      )}

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <FilterTabs
          value={status}
          onChange={setStatus}
          tabs={[
            { key: "all", label: "Toutes", count: counts.all },
            ...Object.entries(STATUS).map(([key, s]) => ({ key, label: s.label, count: counts[key] || 0 })),
          ]}
        />
        <div className="flex gap-2">
          <select value={type} onChange={(e) => setType(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
            <option value="">Tous les types</option>
            {Object.entries(TYPES).map(([key, t]) => <option key={key} value={key}>{t.label}</option>)}
          </select>
          <label className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="N° facture ou commande…"
              className="w-56 rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm dark:border-slate-700" />
          </label>
        </div>
      </div>

      <div className="surface-card overflow-hidden rounded-xl border border-slate-200 bg-white p-0 dark:border-slate-800 dark:bg-slate-900">
        {!filtered.length ? (
          <div className="p-4">
            <EmptyState icon={FileText} title="Aucune facture"
              message={invoices.length ? "Aucune facture ne correspond à ces filtres." : "Les factures apparaissent après la première commande livrée par un prestataire."} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
                  <th className="px-4 py-3">Facture</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Commande</th>
                  <th className="px-4 py-3 text-right">Montant</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3">Émise le</th>
                  <th className="px-4 py-3 text-right">PDF</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => {
                  const s = STATUS[inv.status] || { label: inv.status, tone: "neutral" };
                  const t = TYPES[inv.invoice_type] || { label: inv.invoice_type, tone: "neutral" };
                  return (
                    <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800">
                      <td className="px-4 py-3 font-mono text-xs text-slate-700 dark:text-slate-300">{inv.invoice_number}</td>
                      <td className="px-4 py-3"><StatusPill tone={t.tone}>{t.label}</StatusPill></td>
                      <td className="px-4 py-3 text-xs text-slate-500">{inv.order_reference || String(inv.order_id || "").slice(0, 8) || "—"}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-slate-100">{formatMoney(inv.amount, inv.currency)}</td>
                      <td className="px-4 py-3"><StatusPill tone={s.tone}>{s.label}</StatusPill></td>
                      <td className="px-4 py-3 text-xs text-slate-500">{formatDate(inv.issued_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button type="button" onClick={() => download(inv.id)} disabled={downloading === inv.id}
                          className="icon-button !h-8 !w-8 rounded-lg" title="Télécharger">
                          <Download className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
